import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Clock, X } from "lucide-react";
import { format, isValid } from "date-fns";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";

// Helper to safely format a date string with date and time
function formatSleepDateTime(dateString: string | null | undefined): string {
  if (!dateString) return 'Unknown time';
  const date = new Date(dateString);
  if (!isValid(date)) return 'Unknown time';
  return format(date, 'MMM d, h:mm a');
}
interface SleepReminderCardProps {
  session: any;
  onUpdate: (session: any) => void;
  childName?: string;
}

export function SleepReminderCard({ session, onUpdate, childName }: SleepReminderCardProps) {
  const [dismissing, setDismissing] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const handleDismiss = async () => {
    setDismissing(true);
    try {
      const { error } = await supabase
        .from('sleep_sessions')
        .update({ status: 'complete' } as any)
        .eq('id', session.id);

      if (error) throw error;

      queryClient.invalidateQueries({ queryKey: ['incompleteSessions'] });
      queryClient.invalidateQueries({ queryKey: ['sleepSessions'] });

      toast({
        title: "Reminder dismissed",
        description: "This sleep session is now marked as complete",
      });
    } catch (error) {
      console.error('Error dismissing reminder:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to dismiss reminder",
      });
    } finally {
      setDismissing(false);
    }
  };

  return (
    <Card className="p-4 bg-muted/50 border-primary/20">
      <div className="flex items-start gap-3">
        <Clock className="h-5 w-5 text-primary mt-0.5" />
        <div className="flex-1 space-y-2">
          <div>
            <p className="font-medium text-sm">
              Sleep at {formatSleepDateTime(session.start_time)} is missing some details
            </p>
            <p className="text-sm text-muted-foreground">
              Keep {childName ? `${childName}'s` : 'your'} sleep history accurate
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              onClick={() => onUpdate(session)}
              className="h-8"
            >
              Update now
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={handleDismiss}
              disabled={dismissing}
              className="h-8"
            >
              <X className="h-4 w-4 mr-1" />
              Don't remind me
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
}
